// src/pages/CarbonCredit.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Calculator from '../components/CarbonCredit/Calculator';

const CarbonCredit: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Carbon Credit Initiatives - Impact Generation Africa</title>
        <meta 
          name="description" 
          content="Learn about our carbon credit programs and calculate your carbon offset through tree planting initiatives in Africa." 
        />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="py-5 bg-success text-white"
        style={{ marginTop: '76px' }}
      >
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <motion.h1 
                className="display-5 fw-bold mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Carbon Credit Initiatives
              </motion.h1>
              <motion.p 
                className="lead mb-0"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Offset your carbon footprint while supporting sustainable development in African communities
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <motion.h2 
                className="display-6 fw-bold text-success mb-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                What are Carbon Credits?
              </motion.h2>
              <motion.p 
                className="text-muted mb-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Carbon credits represent the removal or reduction of one ton of carbon dioxide 
                from the atmosphere. Through our tree planting initiatives, we generate verified 
                carbon credits that help combat climate change while creating sustainable 
                livelihoods for local communities.
              </motion.p>
              <motion.div 
                className="row text-center"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="col-4">
                  <div className="text-success h3">15,000+</div>
                  <small className="text-muted">Trees Planted</small>
                </div>
                <div className="col-4">
                  <div className="text-success h3">300+</div>
                  <small className="text-muted">Tons CO₂ Offset</small>
                </div>
                <div className="col-4">
                  <div className="text-success h3">25+</div>
                  <small className="text-muted">Communities</small>
                </div>
              </motion.div>
            </div>
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img 
                  src="/images/carbon-credit-1.jpg" 
                  alt="Carbon credit initiatives" 
                  className="img-fluid rounded shadow"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <motion.h2 
                className="display-6 fw-bold text-success mb-3"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                Triple Impact Benefits
              </motion.h2>
            </div>
          </div>
          <div className="row">
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="card border-0 shadow-sm h-100 text-center">
                <div className="card-body p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>🌍</div>
                  <h5 className="card-title text-success">Environmental</h5>
                  <p className="card-text text-muted">
                    Combat climate change through carbon sequestration, improve air quality, 
                    and restore ecosystems through reforestation.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="card border-0 shadow-sm h-100 text-center">
                <div className="card-body p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>👥</div>
                  <h5 className="card-title text-success">Social</h5>
                  <p className="card-text text-muted">
                    Create sustainable livelihoods for local communities, provide training 
                    opportunities, and enhance food security through agroforestry.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="card border-0 shadow-sm h-100 text-center">
                <div className="card-body p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>💼</div>
                  <h5 className="card-title text-success">Economic</h5>
                  <p className="card-text text-muted">
                    Generate income through carbon credit sales, create green jobs, and 
                    develop sustainable business models for long-term impact.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Calculator />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Participate */}
      <section className="py-5 bg-success text-white">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <motion.h2 
                className="display-6 fw-bold mb-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                How to Get Involved
              </motion.h2>
            </div>
          </div>
          <div className="row">
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-center p-4">
                <div className="display-4 mb-3">🌳</div>
                <h5>Individual Offset</h5>
                <p className="opacity-75">
                  Calculate and offset your personal carbon footprint through our tree planting programs.
                </p>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-center p-4">
                <div className="display-4 mb-3">🏢</div>
                <h5>Corporate Partnership</h5>
                <p className="opacity-75">
                  Help your business achieve carbon neutrality while supporting sustainable development.
                </p>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-center p-4">
                <div className="display-4 mb-3">🤝</div>
                <h5>Community Projects</h5>
                <p className="opacity-75">
                  Support specific community-based reforestation and agroforestry projects.
                </p>
              </div>
            </motion.div>
          </div>
          <div className="row mt-4">
            <div className="col-12 text-center">
              <motion.a
                href="/contact"
                className="btn btn-light btn-lg"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
              >
                Get Started Today
              </motion.a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CarbonCredit;